import { api } from './client'

/**
 * Servidores geridos por SSM que a pessoa pode acessar.
 *
 * A rota (`api.py:ssm_instances_list`) JA filtra por ambiente conforme os
 * codigos `ssm:session:<amb>:access` da pessoa, e instancia sem tag de
 * ambiente reconhecivel NAO vem. Este modulo nao inventa um "sem ambiente"
 * para o que foi excluido: o que a listagem esconde, a sessao tambem recusa —
 * mostrar na tela algo que nao abre seria mentir para quem clica.
 *
 * O que ele PRESERVA e a contagem de `excluidas`, que e o unico jeito de a
 * tela dizer "4 servidores existem e nao aparecem porque nao tem tag" em vez
 * de fingir que a conta tem so uma maquina.
 */

export interface InstanciaSsm {
  instance_id: string
  nome: string | null
  /**
   * Os nomes aqui sao os que `ssm_inventory_service.py` monta, nao traducoes
   * deles. Traduzir para portugues no meio do caminho ja custou caro: a tela
   * lia `ambiente` e `ping_status`, que a rota nunca mandou, e todo servidor
   * saia com o selo de ambiente em branco e "alcancavel" por default.
   * `tests/test_ssm_contrato_frontend.py` reprova a divergencia.
   */
  environment: 'DEV' | 'QA' | 'HML' | 'PRD'
  plataforma: string | null
  ping: string | null
  regiao: string
  tag_ambiente?: { chave: string; valor: string }
}

export interface ListaDeInstancias {
  instancias: InstanciaSsm[]
  /** Quantos foram descartados, por motivo. */
  excluidas: Record<string, number>
  /**
   * Regioes efetivamente consultadas.
   *
   * `DescribeInstanceInformation` e POR REGIAO. Se a task def nao declara as
   * regioes, a rota consulta uma so e devolve lista truncada — que e
   * indistinguivel de uma completa. Por isso a rota expoe isto, e por isso a
   * tela mostra.
   */
  regioes_consultadas: string[]
}

export const ssmApi = {
  listarInstancias: async (tenantId: number): Promise<ListaDeInstancias> => {
    const r = await api.get<ListaDeInstancias>(
      `/v1/tenants/${tenantId}/ssm/instances`,
    )
    return {
      instancias: Array.isArray(r.data?.instancias) ? r.data.instancias : [],
      excluidas: r.data?.excluidas ?? {},
      regioes_consultadas: Array.isArray(r.data?.regioes_consultadas)
        ? r.data.regioes_consultadas
        : [],
    }
  },
}

/** Rotulo legivel para cada motivo de exclusao. */
const MOTIVOS: Record<string, string> = {
  sem_tag: 'sem tag de ambiente',
  ambiente_desconhecido: 'tag com valor que não reconheço',
  sem_permissao: 'sem permissão sua para esse ambiente',
  nao_ec2: 'nó gerenciado que não é EC2',
}

/**
 * Explica os ocultos, um por motivo.
 *
 * Existe porque "a conta tem 3 servidores" e "a conta tem 12 e você vê 3" sao
 * coisas diferentes, e a segunda e a verdadeira. Sem isto, quem procura uma
 * maquina que nao aparece conclui que ela nao existe — e o motivo mais comum
 * (`sem_tag`) nao tem nada a ver com permissao.
 */
export function explicarOcultos(
  excluidas: Record<string, number>,
): { motivo: string; quantos: number }[] {
  return Object.entries(excluidas)
    .filter(([, n]) => n > 0)
    .map(([chave, n]) => ({ motivo: MOTIVOS[chave] ?? chave, quantos: n }))
    .sort((a, b) => b.quantos - a.quantos)
}

/**
 * Aviso quando a cobertura de regiao pode estar incompleta.
 *
 * Lista truncada e indistinguivel de lista completa. Sem o aviso, quem procura
 * um servidor de outra regiao conclui que ele nao existe.
 */
export function avisoDeCobertura(lista: ListaDeInstancias): string | null {
  const n = lista.regioes_consultadas.length
  if (n === 0) {
    return 'Nenhuma região foi consultada — a lista está vazia por configuração, não por ausência de servidores.'
  }
  if (n === 1) {
    return `Só a região ${lista.regioes_consultadas[0]} foi consultada. Servidores em outras regiões não aparecem aqui.`
  }
  return null
}

/**
 * URL WSS da sessao.
 *
 * O BROKER NAO FICA NO MESMO HOST DO CONSOLE, e cair no `window.location.origin`
 * foi o defeito que fez a primeira tentativa real falhar. Medido em 31/08/2026:
 *
 *   console.amazonasinovare.com.br/_broker/...      -> 404
 *   zt.console.amazonasinovare.com.br/_broker/...   -> 401 (existe, pede auth)
 *
 * O sintoma nao foi erro de rota: o WebSocket fechou com **1006**, e a tela
 * disse "a conexao caiu, a plataforma deve ter sido atualizada" — uma mensagem
 * plausivel e errada, que mandaria a pessoa esperar e tentar de novo para
 * sempre.
 *
 * O `VITE_BROKER_BASE_URL` e embutido no build (Dockerfile do frontend) e ja
 * carrega o host certo. Ele e a fonte; a origem da pagina NAO serve de
 * palpite — se a variavel faltar, e melhor falhar dizendo isso.
 */
export function urlDaSessao(tenantId: number, instanceId: string): string {
  const base = import.meta.env.VITE_BROKER_BASE_URL
  if (!base) {
    throw new Error(
      'VITE_BROKER_BASE_URL não está no build — sem ela não dá para saber onde ' +
        'o broker responde, e a origem da página NÃO serve: o console e o ' +
        'broker ficam em hosts diferentes.',
    )
  }
  // SEMPRE wss, mesmo com origem http (dev local): degradar poria SHELL ROOT
  // em texto claro na rede. Se a origem nao for segura, o navegador recusa o
  // upgrade — falhar alto e melhor que degradar em silencio.
  const host = String(base).replace(/^https?:\/\//, '').replace(/\/+$/, '')
  return `wss://${host}/_broker/ssm/${tenantId}/${encodeURIComponent(instanceId)}`
}


export interface ContagemDeSessoes {
  abertas: number
  teto: number
}

/**
 * Quantas sessoes a pessoa tem abertas, e o teto.
 *
 * A tela mostra isto ANTES de a pessoa esbarrar no limite. Sem o aviso, o teto
 * aparece como uma aba que nao abriu — sem relacao obvia com as outras que ja
 * estao abertas, e sem dizer que fechar uma resolve.
 */
export async function contarSessoes(): Promise<ContagemDeSessoes | null> {
  // Bate no CONSOLE, nao no broker. A rota equivalente do broker
  // (`/_broker/ssm/sessoes`) responde sem nenhum `Access-Control-Allow-Origin`,
  // entao o navegador bloqueia a chamada vinda daqui — medido em producao em
  // 01/09/2026: `Failed to fetch`, e o contador nunca carregava. O dado mora no
  // Redis compartilhado pelos dois servicos, entao o console le a mesma chave
  // sem atravessar origem nenhuma.
  try {
    const r = await api.get<Partial<ContagemDeSessoes>>('/v1/ssm/sessoes')
    const d = r.data
    if (typeof d?.abertas !== 'number' || typeof d?.teto !== 'number') return null
    return { abertas: d.abertas, teto: d.teto }
  } catch {
    // Falha aqui NAO pode quebrar a tela: e informacao de conveniencia.
    return null
  }
}

/**
 * O servidor esta alcancavel pelo SSM agora?
 *
 * `ping` vem da AWS e a tela precisa mostrar: um servidor com o agente
 * desconectado tem a MESMA aparencia de um saudavel, e a pessoa so descobre
 * clicando e esperando a sessao falhar.
 */
export function estaAlcancavel(i: { ping?: string | null }): boolean {
  return (i.ping ?? 'Online') === 'Online'
}

/**
 * Filtra a lista pela busca da tela.
 *
 * Vive aqui, e nao dentro do componente, porque o defeito que motivou isto
 * era invisivel de dentro dele: com o nome de campo errado, `i.environment`
 * era undefined e `.toLowerCase()` derrubava a tela no primeiro caractere
 * digitado. Como funcao exportada, o teste roda contra o payload que a rota
 * devolve de verdade.
 *
 * Campo ausente nao derruba nem some da lista — so nao casa por aquele campo.
 */
export function filtrarInstancias<T extends {
  nome?: string | null
  instance_id: string
  environment?: string | null
}>(instancias: readonly T[], consulta: string): readonly T[] {
  const q = consulta.trim().toLowerCase()
  if (!q) return instancias
  return instancias.filter((i) =>
    (i.nome ?? '').toLowerCase().includes(q) ||
    (i.instance_id ?? '').toLowerCase().includes(q) ||
    (i.environment ?? '').toLowerCase().includes(q))
}

/**
 * Rotulo do subprotocolo. Vai junto com o bilhete, e e o unico dos dois que
 * o broker devolve no `Sec-WebSocket-Protocol` da resposta — ecoar o bilhete
 * o poria no log de todo proxy do caminho.
 */
export const PROTOCOLO_SSM = 'consoleai.ssm.v1'

/**
 * Pede ao console um bilhete de uso unico para abrir a sessao.
 *
 * Existe porque um WebSocket de navegador nao aceita cabecalho: o unico canal
 * de credencial no handshake e o `Sec-WebSocket-Protocol`. Ate 01/09/2026 o
 * broker so aceitava o cookie `session`, que vive 15 minutos e nao se renova
 * a partir do console — a tela abria shell por 15 minutos depois do login e
 * depois todo socket morria em 1006.
 */
export async function emitirBilhete(tenantId: number): Promise<string> {
  const r = await api.post<{ bilhete?: string }>(`/v1/tenants/${tenantId}/ssm/ticket`)
  const bilhete = r.data?.bilhete
  if (!bilhete) {
    throw new Error('o console não devolveu bilhete de sessão')
  }
  return bilhete
}

/**
 * Os subprotocolos do handshake, na ordem: rotulo primeiro, bilhete depois.
 *
 * O broker tenta todos, entao a ordem nao decide nada — mas manter o rotulo
 * na frente e o que faz o `accept` ter o que ecoar sem precisar procurar.
 */
export function subprotocolosDaSessao(bilhete: string): string[] {
  return [PROTOCOLO_SSM, bilhete]
}
